import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PagoCuotasFinanciacionPrivadaModel,
  ConstitucionFinanciacionPrivadaModel,
  FinanciacionPrivadaModel,
  DeudaACortoPlazoFinanciacionPrivadaModel
} from './financiacion-privada.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const FINANCIACIONPRIVADA_ENDPOINT = '/financiacionprivada';
const CONSTITUCION_ENDPOINT = '/constitucionfinanciacionprivada';
const DEUDACORTOPLAZO_ENDPOINT = '/deudasacortoplazofinanciacionprivada';
const PAGOCUOTAS_ENDPOINT = '/pagocuotasfinanciacionprivada';
const V2_ENDPOINT = '/api/v2/private-financings';

@Injectable({
  providedIn: 'root',
})
export class FinanciacionPrivadaService {
  constructor(private http: HttpClient) {}

  public downloadFileAttachment(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('financiacionprivadaId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/financiacionprivada/file/downloadexcel', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/vnd.ms-excel' });
    });
  }

  public get(uid: string): Promise<FinanciacionPrivadaModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<FinanciacionPrivadaModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se ha encontrado la financiación privada.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<FinanciacionPrivadaModel>> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<FinanciacionPrivadaModel[]>>(environment.apiUrl + V2_ENDPOINT)),
      'No se pudieron obtener las financiaciones privadas.'
    ).then((list) => {
      const result = new ResultModel<FinanciacionPrivadaModel>();
      result.list = Array.isArray(list) ? list : [];
      result.totalItems = result.list.length;
      return result;
    });
  }

  public add(financiacionprivada: FinanciacionPrivadaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPRIVADA_ENDPOINT, financiacionprivada)
    ).then((response) => response.data);
  }

  public edit(financiacionprivada: FinanciacionPrivadaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPRIVADA_ENDPOINT, financiacionprivada)
    ).then((response) => response.data);
  }

  public editFileAttachment(financiacionprivada: FinanciacionPrivadaModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(
        environment.apiUrl + FINANCIACIONPRIVADA_ENDPOINT + '/file',
        financiacionprivada
      )
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('financiacionprivadaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + FINANCIACIONPRIVADA_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public constitucionList(
    financiacionprivadaId: string
  ): Promise<ConstitucionFinanciacionPrivadaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<ConstitucionFinanciacionPrivadaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(financiacionprivadaId) + '/constitutions'
        )
      ),
      'No se pudieron obtener las constituciones.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public constitucionCreate(
    constitucion: ConstitucionFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, constitucion)
    ).then((response) => response.data);
  }

  public constitucionUpdate(
    constitucion: ConstitucionFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, constitucion)
    ).then((response) => response.data);
  }

  public constitucionDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('constitucionfinanciacionprivadaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public deudaACortoPlazoList(
    deudaacortoplazoUid: string
  ): Promise<DeudaACortoPlazoFinanciacionPrivadaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<DeudaACortoPlazoFinanciacionPrivadaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(deudaacortoplazoUid) + '/short-term-debts'
        )
      ),
      'No se pudieron obtener las deudas a corto plazo.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public deudaACortoPlazoCreate(
    deudaACortoPlazo: DeudaACortoPlazoFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoUpdate(
    deudaACortoPlazo: DeudaACortoPlazoFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('deudasacortoplazofinanciacionprivadaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public pagoCoutasList(
    pagocuotasUid: string
  ): Promise<PagoCuotasFinanciacionPrivadaModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<PagoCuotasFinanciacionPrivadaModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(pagocuotasUid) + '/installment-payments'
        )
      ),
      'No se pudieron obtener los pagos de cuotas.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public pagoCoutasCreate(
    pagoDeCuotas: PagoCuotasFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasUpdate(
    pagoDeCuotas: PagoCuotasFinanciacionPrivadaModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('pagocuotasfinanciacionprivadaId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}